import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, X, ZoomIn, RefreshCw, Trash2, ArrowRight, Search, Check, Camera } from 'lucide-react';
import { sendChatMessageStream } from '../services/botapi';
import type { StreamChunk } from '../services/botapi';

interface Step {
  text: string;
  status: 'pending' | 'done';
}

interface ChatImage {
  b64: string;
  caption?: string;
  status: 'scanning' | 'done';
}

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  steps?: Step[];
  images?: ChatImage[];
  isStreaming?: boolean;
}

const SUGGESTIONS = [
  'How many times did cameras go offline last month, location wise?',
  'Which location has cameras going offline most frequently?',
  'Show recent NVR rack intrusion alerts',
  'Check the latest intrusion — did they actually touch the rack?',
  'NVR storage health at all stations',
  'How many people are at Sattenapalli reception right now?',
];

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function parseMarkdown(src: string): string {
  const lines = src.split('\n');
  let html = '';
  let i = 0;
  let list: 'ul' | 'ol' | null = null;

  const inline = (t: string) =>
    escapeHtml(t)
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
      .replace(/\*([^*]+)\*/g, '<em>$1</em>');

  const closeList = () => {
    if (list) {
      html += `</${list}>`;
      list = null;
    }
  };

  while (i < lines.length) {
    const ln = lines[i];

    // Table detection
    if (
      /^\s*\|.*\|\s*$/.test(ln) &&
      i + 1 < lines.length &&
      /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])
    ) {
      closeList();
      const rows = [];
      rows.push(ln);
      i += 2; // skip header separator line
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i++]);
      }
      const cells = (r: string) =>
        r
          .trim()
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((c) => inline(c.trim()));

      html += '<table><thead><tr>' +
        cells(rows[0]).map((c) => `<th>${c}</th>`).join('') +
        '</tr></thead><tbody>';

      for (let r = 1; r < rows.length; r++) {
        html += '<tr>' +
          cells(rows[r]).map((c) => `<td>${c}</td>`).join('') +
          '</tr>';
      }
      html += '</tbody></table>';
      continue;
    }

    let m: RegExpMatchArray | null;
    if ((m = ln.match(/^(#{1,3})\s+(.*)/))) {
      closeList();
      const level = m[1].length + 1;
      html += `<h${level}>${inline(m[2])}</h${level}>`;
    } else if ((m = ln.match(/^\s*[-*]\s+(.*)/))) {
      if (list !== 'ul') {
        closeList();
        html += '<ul>';
        list = 'ul';
      }
      html += `<li>${inline(m[1])}</li>`;
    } else if ((m = ln.match(/^\s*\d+[.)]\s+(.*)/))) {
      if (list !== 'ol') {
        closeList();
        html += '<ol>';
        list = 'ol';
      }
      html += `<li>${inline(m[1])}</li>`;
    } else if (ln.trim() === '') {
      closeList();
    } else {
      closeList();
      html += `<p>${inline(ln)}</p>`;
    }
    i++;
  }
  closeList();
  return html;
}

export const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleTriggerClick = () => {
    if (!isDragging) {
      setIsOpen(true);
    }
  };
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      sender: 'bot',
      text: "Hello! I'm your monitoring assistant. I can check **live data** from all police stations — camera offline history, intrusion alerts, NVR & storage health — and I can **look at camera evidence images** to tell you whether someone actually tampered with the NVR rack or just walked past. Try one of the questions below.",
    },
  ]);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [activeLightboxImage, setActiveLightboxImage] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState<boolean>(true);

  const feedRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Poll /health endpoint to check bot server status
  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetch(`${window.location.origin}/ai-bot/health`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.status === 'ok') {
            setIsOnline(true);
            return;
          }
        }
        setIsOnline(false);
      } catch (e) {
        console.error('Health check error:', e);
        setIsOnline(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom of chat feed when messages change
  useEffect(() => {
    if (feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [messages, isOpen]);

  // Handle autogrow textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputText]);

  const handleAsk = async (text: string) => {
    if (!text.trim() || isSending) return;
    setIsSending(true);

    // Retrieve userId from local storage 'user' item
    const userInfoStr = localStorage.getItem('user');
    let userId = '';
    if (userInfoStr) {
      try {
        const userInfo = JSON.parse(userInfoStr);
        userId = userInfo.id || '';
      } catch (e) {
        console.error('Failed to parse user info from localStorage:', e);
      }
    }

    // fallback session ID
    const sessionId = userId || 'sid-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);

    // 1. Add User Message
    const userMsgId = 'msg-user-' + Date.now();
    setMessages((prev) => [
      ...prev,
      { id: userMsgId, sender: 'user', text },
    ]);

    setInputText('');

    // 2. Add empty bot message with initial step indicator
    const botMsgId = 'msg-bot-' + (Date.now() + 1);
    setMessages((prev) => [
      ...prev,
      {
        id: botMsgId,
        sender: 'bot',
        text: '',
        steps: [{ text: 'Understanding your question…', status: 'pending' }],
        images: [],
        isStreaming: true,
      },
    ]);

    try {
      await sendChatMessageStream(text, sessionId, (chunk: StreamChunk) => {
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.id !== botMsgId) return msg;

            const updated = { ...msg };

            if (chunk.type === 'step') {
              // Finish previous steps
              const finishedSteps: Step[] = (updated.steps || []).map((s) => ({
                ...s,
                status: 'done' as const,
              }));
              // Append new step
              finishedSteps.push({
                text: chunk.text || '',
                status: 'pending',
              });
              updated.steps = finishedSteps;
            } else if (chunk.type === 'image') {
              // Finish previous steps
              const finishedSteps: Step[] = (updated.steps || []).map((s) => ({
                ...s,
                status: 'done' as const,
              }));
              // Append evidence image
              updated.images = [
                ...(updated.images || []),
                {
                  b64: chunk.b64 || '',
                  caption: chunk.caption || 'camera frame',
                  status: 'scanning' as const,
                },
              ];
              // Add a temporary next step
              finishedSteps.push({
                text: 'AI vision analysing the frame…',
                status: 'pending',
              });
              updated.steps = finishedSteps;
            } else if (chunk.type === 'answer') {
              // Finish all steps
              updated.steps = (updated.steps || []).map((s) => ({
                ...s,
                status: 'done' as const,
              }));
              // Complete all images scan
              updated.images = (updated.images || []).map((img) => ({
                ...img,
                status: 'done' as const,
              }));
              // Set answer content
              updated.text = chunk.text || '';
              updated.isStreaming = false;
            }

            return updated;
          })
        );
      });
    } catch (err) {
      console.error('Chat error:', err);
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.id !== botMsgId) return msg;
          return {
            ...msg,
            isStreaming: false,
            steps: (msg.steps || []).map((s) => ({ ...s, status: 'done' as const })),
            text: `⚠️ Chat request failed: ${err instanceof Error ? err.message : String(err)}`,
          };
        })
      );
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk(inputText);
    }
  };

  const resetChat = () => {
    setMessages([
      {
        id: 'welcome',
        sender: 'bot',
        text: "Hello! I'm your monitoring assistant. I can check **live data** from all police stations — camera offline history, intrusion alerts, NVR & storage health — and I can **look at camera evidence images** to tell you whether someone actually tampered with the NVR rack or just walked past. Try one of the questions below.",
      },
    ]);
  };

  return (
    <>
      {/* Floating Trigger Button in Bottom Right */}
      <motion.button
        drag
        dragMomentum={false}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={() => {
          setTimeout(() => setIsDragging(false), 50);
        }}
        onClick={handleTriggerClick}
        className="fixed bottom-[140px] right-6 z-40 w-14 h-14 bg-white hover:bg-slate-50 border border-[#1A1A2E] rounded-full shadow-2xl flex items-center justify-center cursor-grab active:cursor-grabbing transition-all duration-300 hover:scale-110 active:scale-95 group focus:outline-none touch-none"
        title="AI Monitoring Assistant (Drag to Move)"
      >
        <div className="absolute inset-0 rounded-full bg-[#422568] opacity-50 group-hover:opacity-100 transition-opacity duration-300 animate-ping pointer-events-none" />
        <img
          src="/policeman.png"
          alt="AP Police"
          className="w-12 h-12 object-contain rounded-full border border-slate-100/50 shadow-sm pointer-events-none"
        />
        {/* Pulsing online status indicator badge on the button */}
        <span
          className={`absolute top-0.5 right-0.5 w-3 h-3 border-2 border-white rounded-full shadow-sm ${
            isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
          }`}
          title={isOnline ? 'online' : 'offline'}
        />
      </motion.button>

      {/* Slide-out Drawer Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop overlay & Modal Centered Wrapper */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            >
              {/* Centered Modal Panel */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl h-[650px] max-h-[85vh] bg-white border border-slate-200/80 rounded-3xl shadow-2xl flex flex-col overflow-hidden font-sans cursor-default"
              >
                {/* Header section - Theme of the portal */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <img
                        src="/policeman.png"
                        alt="AP Police Badge"
                        className="w-10 h-10 object-contain rounded-full border border-slate-200 bg-white p-0.5"
                      />
                      <span
                        className={`absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full shadow-sm ${
                          isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'
                        }`}
                        title={isOnline ? 'online' : 'offline'}
                      />
                    </div>
                    <div>
                      <h2 className="text-[13px] font-black text-slate-900 uppercase tracking-widest leading-none flex items-center gap-1.5">
                        <span>OvulScanner AI Assistant</span>
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={resetChat}
                      title="Reset Conversation"
                      className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors focus:outline-none"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setIsOpen(false)}
                      title="Close"
                      className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors focus:outline-none"
                    >
                      <X className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                </div>

                {/* Chat messages feed */}
                <div
                  ref={feedRef}
                  className="flex-1 overflow-y-auto custom-scroll p-5 space-y-5 bg-slate-50/40"
                >
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex gap-3 max-w-[85%] ${
                        msg.sender === 'user' ? 'ml-auto flex-row-reverse' : ''
                      }`}
                    >
                      {/* Avatar */}
                      <div className="shrink-0">
                        {msg.sender === 'user' ? (
                          <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm shadow-md text-white font-bold">
                            <img src='/AP_Police_logo.png' className='w-10 h-10 object-contain' />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-sm shadow-md text-white font-bold">
                            <img src="/policeman.png" className='w-10 h-10 object-contain' />
                          </div>
                        )}
                      </div>

                      {/* Chat Bubble */} 
                      <div className="space-y-2">
                        <div
                          className={`rounded-2xl px-4 py-3 text-[13px] leading-relaxed shadow-sm border ${
                            msg.sender === 'user'
                              ? 'bg-[#422568] border-[#422568]/20 text-white rounded-tr-none'
                              : 'bg-white border-slate-200 text-slate-800 rounded-tl-none'
                          }`}
                        >
                          {msg.sender === 'user' ? (
                            <div className="whitespace-pre-wrap font-medium">{msg.text}</div>
                          ) : msg.text ? (
                            <div
                              className="chat-answer prose prose-slate max-w-none text-[13px] font-medium"
                              dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.text) }}
                            />
                          ) : (
                            <div className="flex items-center gap-1.5 py-1 text-slate-400 select-none">
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]" />
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]" />
                              <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                            </div>
                          )}
                        </div>

                        {/* Display progress steps for Bot responses */}
                        {msg.sender === 'bot' && msg.steps && msg.steps.length > 0 && (
                          <div className="px-2 py-1 space-y-1.5">
                            {msg.steps.map((step, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center gap-2 text-[11px] font-semibold transition-colors duration-300 ${
                                  step.status === 'pending'
                                    ? 'text-violet-600'
                                    : 'text-slate-400'
                                }`}
                              >
                                <span className="flex-shrink-0 flex items-center justify-center">
                                  {step.status === 'pending' ? (
                                    <span className="w-3.5 h-3.5 border-2 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
                                  ) : (
                                    <Check className="w-3.5 h-3.5 text-emerald-500" strokeWidth={3} />
                                  )}
                                </span>
                                <span>{step.text}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Display evidence images */}
                        {msg.sender === 'bot' && msg.images && msg.images.length > 0 && (
                          <div className="grid gap-3 mt-2">
                            {msg.images.map((img, idx) => (
                              <div
                                key={idx}
                                className={`evidence relative rounded-xl overflow-hidden border border-slate-200 shadow-sm max-w-sm group ${
                                  img.status === 'scanning' ? 'scanning' : ''
                                }`}
                              >
                                {/* Scan Badge */}
                                <div className="absolute top-2.5 left-2.5 z-10 px-2.5 py-1 rounded-full bg-slate-950/80 backdrop-blur-md border border-white/10 text-[10px] font-black tracking-wider uppercase flex items-center gap-1.5">
                                  {img.status === 'scanning' ? (
                                    <span className="text-amber-400 animate-pulse flex items-center gap-1">
                                      <Search className="w-3 h-3" />
                                      <span>AI Vision Scanning…</span>
                                    </span>
                                  ) : (
                                    <span className="text-emerald-400 flex items-center gap-1">
                                      <Check className="w-3 h-3" />
                                      <span>Analysed by AI Vision</span>
                                    </span>
                                  )}
                                </div>

                                <img
                                  src={`data:image/jpeg;base64,${img.b64}`}
                                  alt={img.caption || 'evidence image'}
                                  className="w-full object-cover max-h-56 cursor-zoom-in group-hover:scale-[1.02] transition-transform duration-300"
                                  onClick={() => setActiveLightboxImage(`data:image/jpeg;base64,${img.b64}`)}
                                />

                                <div className="bg-white/95 backdrop-blur px-3 py-2 border-t border-slate-100 flex items-center justify-between">
                                  <span className="text-[10.5px] font-bold text-slate-500 truncate pr-4 flex items-center gap-1">
                                    <Camera className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Evidence: {img.caption || 'camera frame'}</span>
                                  </span>
                                  <span className="text-[9.5px] font-black text-[#422568] uppercase tracking-wider flex items-center gap-1 shrink-0 select-none">
                                    <span>Enlarge</span>
                                    <ZoomIn className="w-3 h-3" />
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Suggestions and Text Input Area */}
                <div className="border-t border-slate-150 p-4 bg-white">
                  {/* Query Suggestion Chips */}
                  {messages.length <= 1 && (
                    <div className="mb-4">
                      <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2.5">
                        Suggested Queries
                      </p>
                      <div className="flex gap-2 flex-wrap max-h-36 overflow-y-auto custom-scroll pr-1">
                        {SUGGESTIONS.map((chip, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleAsk(chip)}
                            disabled={isSending}
                            className="text-[10px] font-bold text-left px-3 py-2 bg-slate-50 hover:bg-[#422568]/5 text-slate-600 hover:text-[#422568] border border-slate-200/60 rounded-xl transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed leading-snug flex items-center gap-2 group"
                          >
                            <span className="flex-1">{chip}</span>
                            <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#422568] transition-colors flex-shrink-0" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Input box */}
                  <div className="flex gap-2.5 items-end">
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isSending}
                      placeholder="Ask about cameras, NVR rack health, intrusion alerts..."
                      className="flex-1 max-h-32 bg-slate-50/50 hover:bg-slate-50 border border-slate-500 focus:border-[#422568] focus:bg-white text-[13px] font-semibold text-slate-800 placeholder-slate-400 rounded-2xl px-4 py-3 outline-none resize-none transition-all focus:ring-4 focus:ring-[#422568]/5"
                    />
                    <button
                      onClick={() => handleAsk(inputText)}
                      disabled={!inputText.trim() || isSending}
                      className="w-11 h-11 rounded-2xl bg-[#422568] hover:bg-[#422568]/95 text-white flex items-center justify-center cursor-pointer transition-all active:scale-95 shadow-md shadow-[#422568]/15 disabled:opacity-40 disabled:scale-100 disabled:cursor-default"
                      title="Send message"
                    >
                      {isSending ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4.5 h-4.5" />
                      )}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Evidence Image Lightbox Zoom Overlay */}
      {activeLightboxImage && (
        <div
          onClick={() => setActiveLightboxImage(null)}
          className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[99999] cursor-zoom-out animate-fade-in"
        >
          <motion.img
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            src={activeLightboxImage}
            alt="Evidence Detail Fullscreen"
            className="max-w-[94vw] max-h-[92vh] object-contain rounded-xl shadow-2xl"
          />
        </div>
      )}
    </>
  );
};
